import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/layout/Topbar';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import { useToast } from '../hooks/useToast';
import api from '../services/api';
import projectsApi from '../services/projectsApi';

interface Agent {
  id: number;
  name?: string;
  agent_name?: string;
  persona?: string;
  expertise?: string;
}

interface ProjectFormData {
  title: string;
  description: string;
  category: string;
  project_type: 'mission' | 'research';
  visibility: 'public' | 'private' | 'restricted';
  agent_ids: number[];
}

export default function CreateProject(): JSX.Element {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [projectType, setProjectType] = useState<'research' | 'work'>('research');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    description: '',
    category: 'research',
    project_type: 'research',
    visibility: 'public',
    agent_ids: []
  });

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await api.get('/agents');
        setAgents(response.data as Agent[] || []);
      } catch (err) {
        console.error('Failed to load agents:', err);
      }
    };
    void loadAgents();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = (): boolean => {
    if (!formData.title || formData.title.length < 5) {
      error('Project title must be at least 5 characters');
      return false;
    }
    if (!formData.category) {
      error('Please select a category');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.description || formData.description.length < 20) {
      error('Description must be at least 20 characters');
      return false;
    }
    if (formData.agent_ids.length === 0) {
      error('Please select at least 1 agent');
      return false;
    }
    return true;
  };

  const toggleAgent = (agentId: number) => {
    console.log('Toggle agent:', agentId, 'Current:', formData.agent_ids);
    setFormData(prev => ({
      ...prev,
      agent_ids: prev.agent_ids.includes(agentId)
        ? prev.agent_ids.filter(id => id !== agentId)
        : [...prev.agent_ids, agentId]
    }));
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await projectsApi.createProject(formData);
      success('Project created successfully!');
      navigate(`/projects/${response.project_id}`);
    } catch (err) {
      const errorObj = err as { response?: { data?: { detail?: string } } }
      error(errorObj.response?.data?.detail ?? 'Failed to create project');
      setLoading(false)
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--body-bg)' }}>
      <Topbar />
      <div style={{ flex: 1, overflow: 'auto', padding: '3rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Create New Project
            </h1>
            <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>
              Choose your project type and let AI agents help you achieve your goals
            </p>
          </div>

          {/* Project Type Tabs */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '3rem',
            background: 'var(--card-bg)',
            padding: '0.5rem',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            <button
              onClick={() => setProjectType('research')}
              style={{
                flex: 1,
                padding: '1.5rem',
                border: 'none',
                borderRadius: '8px',
                background: projectType === 'research' ? 'var(--accent-blue)' : 'transparent',
                color: projectType === 'research' ? '#fff' : 'var(--text-primary)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '1.125rem',
                fontWeight: 600
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <span>Research</span>
                <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>Long-term exploration</span>
              </div>
            </button>

            <button
              onClick={() => navigate('/work/create')}
              style={{
                flex: 1,
                padding: '1.5rem',
                border: 'none',
                borderRadius: '8px',
                background: 'transparent',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '1.125rem',
                fontWeight: 600
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
                <span>Work</span>
                <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>Task-based projects</span>
              </div>
            </button>
          </div>

          {/* Research Form */}
          {projectType === 'research' && (
            <div style={{ background: 'var(--card-bg)', borderRadius: '16px', padding: '2rem', border: '1px solid var(--border-color)' }}>
              {/* Progress Steps */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3rem', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '20px', left: '10%', right: '10%', height: '2px', background: 'var(--border-color)', zIndex: 0 }} />
                {[1, 2, 3].map(s => (
                  <div key={s} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: step >= s ? 'var(--accent-blue)' : 'var(--card-bg)',
                      border: `2px solid ${step >= s ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: step >= s ? '#fff' : 'var(--text-secondary)',
                      fontWeight: 600,
                      marginBottom: '0.5rem'
                    }}>
                      {s}
                    </div>
                    <span style={{ fontSize: '0.875rem', color: step >= s ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: step >= s ? 600 : 400 }}>
                      {s === 1 ? 'Basic Info' : s === 2 ? 'Details & Agents' : 'Review'}
                    </span>
                  </div>
                ))}
              </div>

              {/* Step 1: Basic Info */}
              {step === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <Input
                    label="Project Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    fullWidth
                    placeholder="Enter a descriptive title for your research project"
                  />
                  <Input
                    as="select"
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    fullWidth
                    options={[
                      { value: 'research', label: 'Research & Development' },
                      { value: 'development', label: 'Software Development' },
                      { value: 'design', label: 'Design & UX' },
                      { value: 'data', label: 'Data Science & AI' }
                    ]}
                  />
                  <Input
                    as="select"
                    label="Visibility"
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleChange}
                    fullWidth
                    options={[
                      { value: 'public', label: 'Public - Anyone can view' },
                      { value: 'restricted', label: 'Restricted - Invite only' },
                      { value: 'private', label: 'Private - Only you' }
                    ]}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                    <Button onClick={() => navigate('/projects')} variant="secondary">Cancel</Button>
                    <Button onClick={handleNext} variant="primary">Next Step</Button>
                  </div>
                </div>
              )}

              {/* Step 2: Details & Agents */}
              {step === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <Input
                    as="textarea"
                    label="Project Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    fullWidth
                    rows={6}
                    placeholder="Describe your research goals, methodology, and expected outcomes..."
                  />
                  <div>
                    <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>
                      Select AI Agents ({formData.agent_ids.length} selected)
                    </label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '1rem',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      padding: '1rem',
                      background: 'var(--body-bg)',
                      borderRadius: '8px'
                    }}>
                      {agents.map(agent => (
                        <div
                          key={agent.id}
                          onClick={() => toggleAgent(agent.id)}
                          style={{
                            padding: '1.25rem',
                            border: formData.agent_ids.includes(agent.id) ? '2px solid var(--accent-blue)' : '1px solid var(--border-color)',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            background: formData.agent_ids.includes(agent.id) ? 'rgba(102, 126, 234, 0.1)' : 'var(--card-bg)',
                            transition: 'all 0.2s',
                            position: 'relative'
                          }}
                        >
                          {formData.agent_ids.includes(agent.id) && (
                            <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', width: '24px', height: '24px', borderRadius: '50%', background: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </div>
                          )}
                          <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem', color: 'var(--text-primary)' }}>{agent.agent_name ?? agent.name}</div>
                          {agent.persona && <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>{agent.persona}</div>}
                          {agent.expertise && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {agent.expertise.split(',').slice(0, 3).map((exp, i) => (
                                <span key={i} style={{ padding: '0.25rem 0.5rem', background: 'var(--body-bg)', borderRadius: '4px' }}>{exp.trim()}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                    <Button onClick={() => setStep(1)} variant="secondary">Back</Button>
                    <Button onClick={handleNext} variant="primary">Review Project</Button>
                  </div>
                </div>
              )}
              {/* Step 3: Review */}
              {step === 3 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <div style={{ padding: '2rem', background: 'var(--body-bg)', borderRadius: '12px' }}>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)' }}>{formData.title}</h2>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                      <span style={{ padding: '0.5rem 1rem', background: 'var(--accent-blue)', color: '#fff', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 600 }}>{formData.category}</span>
                      <span style={{ padding: '0.5rem 1rem', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 600 }}>{formData.visibility}</span>
                    </div>
                    <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '2rem' }}>{formData.description}</p>
                    <div>
                      <strong style={{ display: 'block', marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-primary)' }}>Selected Agents ({formData.agent_ids.length})</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                        {agents.filter(a => formData.agent_ids.includes(a.id)).map(agent => (
                          <div key={agent.id} style={{ padding: '0.75rem 1.25rem', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-blue)' }} />
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{agent.agent_name ?? agent.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setStep(2)} variant="secondary">Back</Button>
                    <Button onClick={() => void handleSubmit()} variant="success" loading={loading}>Create Project</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
